import { ConfigProvider } from 'antd'
import Sider from 'antd/es/layout/Sider'
import { EXPLORER_HIGHLIGHT_COLOR } from 'features/drilldown'
import React, { createContext, FC, useCallback, useMemo, useState } from 'react'
import { GeckoTheme } from 'styles/GeckoTheme'

export interface HoverCollapseSiderProps {
  children: React.ReactNode
  style?: React.CSSProperties
}

/**
 * An Ant.D Sider which collapses by default, and then uncollapses on
 * on mouse-enter.
 * Has some Gecko-specific styling.
 *
 * Children will have a `CollapseContext` available to them to allow them
 * to adjust a "Collapse" version of themselves.
 */
export const HoverCollapseSider: FC<HoverCollapseSiderProps> = ({
  style,
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true)

  const handleMouseEnter = useCallback(() => setIsCollapsed(false), [])
  const handleMouseLeave = useCallback(() => setIsCollapsed(true), [])

  const contextValue = useMemo(
    () => ({
      isCollapsed,
    }),
    [isCollapsed]
  )
  return (
    <CollapseContext.Provider value={contextValue}>
      <ConfigProvider
        theme={{
          components: {
            Layout: {
              siderBg: EXPLORER_HIGHLIGHT_COLOR,
            },
            Menu: {
              darkItemBg: 'transparent',
              darkItemSelectedBg: GeckoTheme.colors.grayscale[400],
            },
          },
        }}>
        <Sider
          collapsible
          collapsed={isCollapsed}
          trigger={null}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          width={200}
          style={{
            ...style,
            borderRadius: 10,
            padding: 5,
          }}>
          {children}
        </Sider>
      </ConfigProvider>
    </CollapseContext.Provider>
  )
}

type CollapseContextType = { isCollapsed: boolean }
export const CollapseContext = createContext<CollapseContextType>({
  isCollapsed: false,
})
